import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
} from "lucide-react";
import { getEffectiveClientId } from "../../api/authClient";
import {
  downloadSafetyReport,
  fetchSafetyReports,
  getSafetyReportDateRange,
  parseSafetyReportsResponse,
  type SafetyReportRow,
} from "../../api/safetyReports";
import { useAuthMe } from "../../hooks/useAuthMe";
import { resolveApplicationId } from "../../utils/resolveApplicationId";

const TIME_RANGE_OPTIONS = [
  { label: "Last 7 days", value: "7" },
  { label: "Last 14 days", value: "14" },
  { label: "Last 30 days", value: "30" },
  { label: "Last 60 days", value: "60" },
  { label: "Last 90 days", value: "90" },
] as const;

const PAGE_SIZE = 10;

function formatReportDate(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getApiErrorMessage(err: unknown): string {
  const e = err as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    e?.message ||
    "Failed to load safety reports."
  );
}

export default function SafetyReports() {
  const { data: me, isLoading: meLoading } = useAuthMe();
  const [timeRange, setTimeRange] =
    useState<(typeof TIME_RANGE_OPTIONS)[number]["value"]>("30");
  const [page, setPage] = useState(1);

  const applicationId = useMemo(() => resolveApplicationId(me), [me]);

  const clientId = useMemo(() => {
    if (me) {
      return String(me.client_id ?? me.clientId ?? me.id ?? me.user_id ?? "").trim();
    }
    return getEffectiveClientId();
  }, [me]);

  const dateRange = useMemo(
    () => getSafetyReportDateRange(Number(timeRange)),
    [timeRange]
  );

  const reportsQuery = useQuery({
    queryKey: [
      "safety-reports",
      clientId,
      applicationId,
      dateRange.from,
      dateRange.to,
      page,
    ],
    queryFn: async () => {
      const payload = await fetchSafetyReports({
        clientId,
        applicationId,
        from: dateRange.from,
        to: dateRange.to,
        page,
        pageSize: PAGE_SIZE,
      });
      return parseSafetyReportsResponse(payload);
    },
    enabled: Boolean(clientId && applicationId),
  });

  const reports: SafetyReportRow[] = reportsQuery.data?.reports ?? [];
  const totalCount = reportsQuery.data?.total ?? 0;
  const pageSize = reportsQuery.data?.pageSize ?? PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const reportCount = totalCount;
  const loading = meLoading || reportsQuery.isLoading;
  const fetching = reportsQuery.isFetching;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalCount);
  const configReady = !meLoading;
  const missingClientId = configReady && !clientId;
  const missingApplicationId = configReady && !applicationId;
  const missingConfig = missingClientId || missingApplicationId;
  const fetchError =
    reportsQuery.isError && !missingConfig ? getApiErrorMessage(reportsQuery.error) : null;

  const openReport = (report: SafetyReportRow) => {
    window.open(report.safetyReport, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <Link
        to="/settings"
        className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Settings
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800/70 dark:bg-[#0B0F19]">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6 dark:border-slate-800/70">
          <div className="flex min-w-0 items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                  Safety Reports
                </h1>
                <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-blue-600 px-2 text-xs font-semibold text-white">
                  {loading ? "…" : reportCount}
                </span>
              </div>
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                Report Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:shrink-0">
            <span className="text-sm text-slate-500 dark:text-slate-400">Time Range:</span>
            <div className="relative">
              <select
                value={timeRange}
                onChange={(e) => {
                  setTimeRange(e.target.value as (typeof TIME_RANGE_OPTIONS)[number]["value"]);
                  setPage(1);
                }}
                disabled={loading || fetching}
                className="appearance-none rounded-lg border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/15 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-[#141925] dark:text-slate-100"
              >
                {TIME_RANGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] gap-4 border-b border-slate-200 bg-slate-50 px-5 py-3 text-[11px] font-medium uppercase tracking-wider text-slate-500 sm:px-6 dark:border-slate-800/70 dark:bg-[#141925]/80 dark:text-slate-400">
          <span>Type</span>
          <span>Report ID</span>
          <span className="text-right sm:text-left">Created</span>
          <span className="text-right">Actions</span>
        </div>

        {!configReady || loading ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-6 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading safety reports…</p>
          </div>
        ) : missingConfig ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center px-6 py-16 text-center">
            <p className="text-sm text-red-600 dark:text-red-300">
              {missingClientId
                ? "Could not resolve your client profile. Sign in again."
                : "Could not resolve application ID. Set REACT_APP_APPLICATION_ID in surveillance/.env and restart the dev server, or ensure this app is assigned to your account."}
            </p>
          </div>
        ) : fetchError ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <p className="text-sm text-red-600 dark:text-red-300">{fetchError}</p>
            <button
              type="button"
              onClick={() => reportsQuery.refetch()}
              className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-900 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-[#141925] dark:text-slate-100 dark:hover:bg-[#1C202F]/55"
            >
              Retry
            </button>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex min-h-[320px] flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-500/40 dark:bg-blue-500/15">
              <FileText className="h-10 w-10" strokeWidth={1.25} />
            </div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              No Safety Reports Available
            </h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
              Safety reports will appear here once they are generated. Check back later for
              updates.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-200 dark:divide-slate-800/70">
              {reports.map((report) => (
                <li
                  key={report.id}
                  className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] items-center gap-4 px-5 py-4 text-sm sm:px-6"
                >
                  <div className="min-w-0">
                    <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                      {report.type}
                    </span>
                    <span className="block truncate text-xs text-slate-500 dark:text-slate-500">
                      {report.fileName}
                    </span>
                  </div>
                  <span
                    className="truncate font-mono text-[13px] text-slate-600 dark:text-slate-300"
                    title={report.id}
                  >
                    {report.id}
                  </span>
                  <span className="truncate text-right text-slate-600 sm:text-left dark:text-slate-400">
                    {formatReportDate(report.createdAt)}
                  </span>
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => downloadSafetyReport(report.safetyReport, report.fileName)}
                      className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                      aria-label={`Download ${report.fileName}`}
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openReport(report)}
                      className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#1C202F]/55 dark:hover:text-slate-200"
                      aria-label={`Open ${report.fileName}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-col gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 dark:border-slate-800/70">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Showing{" "}
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {rangeStart}–{rangeEnd}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-900 dark:text-slate-200">
                  {totalCount}
                </span>{" "}
                reports
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || fetching}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-[#141925] dark:text-slate-200 dark:hover:bg-[#1C202F]/55"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </button>
                <span className="min-w-[88px] text-center text-xs text-slate-500 dark:text-slate-400">
                  Page{" "}
                  <span className="font-semibold text-slate-900 dark:text-slate-200">{page}</span> of{" "}
                  <span className="font-semibold text-slate-900 dark:text-slate-200">
                    {totalPages}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || fetching}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-[#141925] dark:text-slate-200 dark:hover:bg-[#1C202F]/55"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
